import {useMapLayerProvider, useWMSMapLayerProvider} from "base/component/geo";
import {createContext, useContext} from "react";

import {useMspeezfitnessLayer} from "app/mspeezfitness/MspeezfitnessLayer";
import {MspeezfitnessService} from "app/mspeezfitness/MspeezfitnessService";
import {useNm12Layer} from "app/nm12/Nm12Layer";
import {Nm12Service} from "app/nm12/Nm12Service";
import {useEezLayer} from "app/eez/EezLayer";
import {EezService} from "app/eez/EezService";
import {useZoningareasLayer} from "app/zoningareas/ZoningareasLayer";
import {ZoningareasService} from "app/zoningareas/ZoningareasService";
import useCoordinationindexesLayer from "app/coordinationindexes/CoordinationindexesLayer";
import {CoordinationindexesService} from "app/coordinationindexes/CoordinationindexesService";
import {useFishinggvaMapProvider} from "app/rasters/FishinggvaMapProvider";
import {useMaritimetransportemploymentMapProvider} from "app/rasters/MaritimetransportemploymentMapProvider";
import {useMaritimetransportgvaMapProvider} from "app/rasters/MaritimetransportgvaMapProvider";
import {useFishingemploymentMapProvider} from "app/rasters/FishingemploymentMapProvider";
import {useNavigate} from "react-router-dom";
import {useWorldLayer} from "app/world/WorldLayer";
import {WorldService} from "app/world/WorldService";
import {useCountriesLayer} from "app/countries/CountriesLayer";
import {CountriesService} from "app/countries/CountriesService";
import {useRegionsLayer} from "app/regions/RegionsLayer";
import {RegionsService} from "app/regions/RegionsService";
import {useTileLayerObject} from "base/map";
import {
    routedensity_01seasonalConfig,
    routedensity_02seasonalConfig,
    routedensity_03seasonalConfig,
    routedensity_04seasonalConfig,
    routedensity_05seasonalConfig,
} from "app/routedensitywms";
import {
    portvesselsConfig,
    portvesselsbytypeConfig,
    portgoodsConfig,
    portpassengersConfig,
} from "app/mainportswms";

let ListContext = createContext(null);

export function MainMapProvider({config = null, children}) {
    const navigate = useNavigate();

    // console.log("******* Main Map Provider");
    const mspeezfitnessLayer = useMspeezfitnessLayer();
    const [mspeezfitnessMapProvider] = useMapLayerProvider(
        MspeezfitnessService,
        mspeezfitnessLayer,
        {
            visible: false,
            included: true,
            ...config?.mspeezfitness,
        }
    );

    const zoningareasLayer = useZoningareasLayer();
    const [zoningareasMapProvider] = useMapLayerProvider(
        ZoningareasService,
        zoningareasLayer,
        {
            visible: false,
            included: true,
            discriminator: zoningareasLayer.legend.discriminators.SEA_USE_NAME,
            customClickHandler: ({feature}) =>
                navigate(`info/zoningareas/${feature.id}`),
            ...config?.zoningareas,
        }
    );

    // const basemapLayer = useBasemapLayer();
    // const [basemapMapProvider] = useMapLayerProvider(BasemapService, basemapLayer, {
    //     visible: true,
    //     included: true,
    //     discriminator: basemapLayer.legend.discriminators.NAME,
    //     ...config?.basemap,
    // });

    const worldLayer = useWorldLayer();
    const [worldMapProvider] = useMapLayerProvider(WorldService, worldLayer, {
        visible: true,
        included: true,
        ...config?.world,
    });
    const countriesLayer = useCountriesLayer();
    const [countriesMapProvider] = useMapLayerProvider(
        CountriesService,
        countriesLayer,
        {
            visible: true,
            included: true,
            ...config?.countries,
        }
    );
    const regionsLayer = useRegionsLayer();
    const [regionsMapProvider] = useMapLayerProvider(RegionsService, regionsLayer, {
        visible: true,
        included: true,
        ...config?.regions,
    });

    const coordinationindexesLayer = useCoordinationindexesLayer();
    const [coordinationindexesMapProvider] = useMapLayerProvider(
        CoordinationindexesService,
        coordinationindexesLayer,
        {
            visible: false,
            included: true,
            discriminator: coordinationindexesLayer.legend.discriminators.CI,
            customClickHandler: ({feature}) =>
                navigate(`info/coordinationindexes/${feature.id}`),
            ...config?.coordinationindexes,
        }
    );

    const nm12Layer = useNm12Layer();
    const [nm12MapProvider] = useMapLayerProvider(Nm12Service, nm12Layer, {
        visible: false,
        included: true,
        ...config?.nm12,
    });

    const eezLayer = useEezLayer();
    const [eezMapProvider] = useMapLayerProvider(EezService, eezLayer, {
        visible: false,
        included: true,
        customClickHandler: ({feature}) => navigate(`info/eez/${feature.id}`),
        ...config?.eez,
    });

    const fishingemploymentMapProvider = useFishingemploymentMapProvider({
        visible: false,
        included: true,
        ...config?.fishingemployment,
    });
    const fishinggvaMapProvider = useFishinggvaMapProvider({
        visible: false,
        included: true,
        ...config?.fishinggva,
    });
    const maritimetransportemploymentMapProvider =
        useMaritimetransportemploymentMapProvider({visible: false, included: true});
    const maritimetransportgvaMapProvider = useMaritimetransportgvaMapProvider({
        visible: false,
        included: true,
        ...config?.maritimetransportemployment,
    });

    const routedensity_01seasonal = useTileLayerObject(routedensity_01seasonalConfig);
    const [routedensity_01seasonalProvider] = useWMSMapLayerProvider(
        routedensity_01seasonal,
        {
            visible: false,
            included: true,
            ...config?.routedensity_01seasonal,
        }
    );

    const routedensity_02seasonal = useTileLayerObject(routedensity_02seasonalConfig);
    const [routedensity_02seasonalProvider] = useWMSMapLayerProvider(
        routedensity_02seasonal,
        {
            visible: false,
            included: true,
            ...config?.routedensity_02seasonal,
        }
    );

    const routedensity_03seasonal = useTileLayerObject(routedensity_03seasonalConfig);
    const [routedensity_03seasonalProvider] = useWMSMapLayerProvider(
        routedensity_03seasonal,
        {
            visible: false,
            included: true,
            ...config?.routedensity_03seasonal,
        }
    );

    const routedensity_04seasonal = useTileLayerObject(routedensity_04seasonalConfig);
    const [routedensity_04seasonalProvider] = useWMSMapLayerProvider(
        routedensity_04seasonal,
        {
            visible: false,
            included: true,
            ...config?.routedensity_04seasonal,
        }
    );

    const routedensity_05seasonal = useTileLayerObject(routedensity_05seasonalConfig);
    const [routedensity_05seasonalProvider] = useWMSMapLayerProvider(
        routedensity_05seasonal,
        {
            visible: false,
            included: true,
            ...config?.routedensity_05seasonal,
        }
    );

    const portvessels = useTileLayerObject(portvesselsConfig);
    const [portvesselsProvider] = useWMSMapLayerProvider(portvessels, {
        visible: false,
        included: true,
        ...config?.portvessels,
    });
    const portvesselsbytype = useTileLayerObject(portvesselsbytypeConfig);
    const [portvesselsbytypeProvider] = useWMSMapLayerProvider(portvesselsbytype, {
        visible: false,
        included: true,
        ...config?.portvesselsbytype,
    });
    const portgoods = useTileLayerObject(portgoodsConfig);
    const [portgoodsProvider] = useWMSMapLayerProvider(portgoods, {
        visible: false,
        included: true,
        ...config?.portgoods,
    });
    const portpassengers = useTileLayerObject(portpassengersConfig);
    const [portpassengersProvider] = useWMSMapLayerProvider(portpassengers, {
        visible: false,
        included: true,
        ...config?.portpassengers,
    });

    const layerProviders = {
        // basemapMapProvider,
        worldMapProvider,
        countriesMapProvider,
        regionsMapProvider,
        zoningareasMapProvider,
        mspeezfitnessMapProvider,
        coordinationindexesMapProvider,
        nm12MapProvider,
        eezMapProvider,
        fishingemploymentMapProvider,
        fishinggvaMapProvider,
        maritimetransportemploymentMapProvider,
        maritimetransportgvaMapProvider,
        routedensity_01seasonalProvider,
        routedensity_02seasonalProvider,
        routedensity_03seasonalProvider,
        routedensity_04seasonalProvider,
        routedensity_05seasonalProvider,
        portvesselsProvider,
        portvesselsbytypeProvider,
        portgoodsProvider,
        portpassengersProvider,
    };

    const getAllLayerProviders = () => {
        const includedLayers = Object.entries(layerProviders).map(
            ([, layerProvider]) => layerProvider
        );
        return includedLayers;
    };

    const getIncludedLayerProviders = () => {
        return getAllLayerProviders().filter(
            layerProvider => layerProvider.included === true
        );
    };

    const getMenuSections = () => {
        return [
            {
                name: "BASEMAP",
                layers: [worldMapProvider, countriesMapProvider, regionsMapProvider],
            },
            {
                name: "MARITIME SPATIAL PLANNING",
                layers: [zoningareasMapProvider],
            },
            {
                name: "SOCIOECONOMIC",
                layers: [
                    fishinggvaMapProvider,
                    fishingemploymentMapProvider,
                    maritimetransportgvaMapProvider,
                    maritimetransportemploymentMapProvider,
                ],
            },
            {
                name: "GOVERNANCE",
                layers: [mspeezfitnessMapProvider, coordinationindexesMapProvider],
            },
            {
                name: "TRANSVERSAL INFORMATION",
                layers: [nm12MapProvider, eezMapProvider],
                children: [
                    {
                        name: "Route Density (EMSA) Seasonal Totals",
                        layers: [
                            routedensity_01seasonalProvider,
                            routedensity_02seasonalProvider,
                            routedensity_03seasonalProvider,
                            routedensity_04seasonalProvider,
                            routedensity_05seasonalProvider,
                        ],
                    },
                    {
                        name: "Main Ports (1997 - 2024)",
                        layers: [
                            portvesselsProvider,
                            portvesselsbytypeProvider,
                            portgoodsProvider,
                            portpassengersProvider,
                        ],
                    },
                ],
            },
        ];
    };

    return (
        <ListContext.Provider
            value={{
                ...layerProviders,
                getAllLayerProviders,
                getIncludedLayerProviders,
                getMenuSections,
            }}
        >
            {children}
        </ListContext.Provider>
    );
}

export function useMainMapProvider() {
    return useContext(ListContext);
}
